import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Eye, Upload, Image, X, Bold, Palette, Download, Wrench, List, ListOrdered, Heading1, Heading2, Table } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color options for text
const TEXT_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Dark Gray', value: '#374151' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Purple', value: '#7c3aed' },
];

const BlogManager = ({ onStatsUpdate }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [fixingIds, setFixingIds] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    excerpt: '',
    content: '',
    image: '',
    published: true
  });
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  const handleSeedPosts = async () => {
    if (!window.confirm('This will add sample blog posts to your database. Posts with the same title will be skipped. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/blog/seed-sample-posts`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed posts");
    } finally {
      setSeeding(false);
    }
  };

  const handleFixPostIds = async () => {
    if (!window.confirm('This will fix posts with invalid IDs (like "1", "2", etc.) by assigning proper unique IDs. Continue?')) {
      return;
    }
    
    setFixingIds(true);
    try {
      const response = await axios.post(`${API}/admin/blog/fix-post-ids`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix post IDs");
    } finally {
      setFixingIds(false);
    }
  };

  const handleFixImageUrls = async () => {
    if (!window.confirm('This will fix image URLs that point to old domains by converting them to relative paths. Continue?')) {
      return;
    }
    
    setFixingIds(true);
    try {
      const response = await axios.post(`${API}/admin/blog/fix-image-urls`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix image URLs");
    } finally {
      setFixingIds(false);
    }
  };

  // Handle paste event to convert rich text to HTML
  const handlePaste = (e) => {
    const clipboardData = e.clipboardData;
    
    // Check if there's HTML content
    const htmlContent = clipboardData.getData('text/html');
    if (htmlContent) {
      e.preventDefault();
      
      // Parse and clean the HTML
      const cleanedHtml = cleanPastedHtml(htmlContent);
      
      // Insert at cursor position
      const textarea = contentRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = formData.content.substring(0, start) + cleanedHtml + formData.content.substring(end);
      setFormData({ ...formData, content: newContent });
      
      toast.success('Formatted content pasted!');
    }
    // If no HTML, let the default paste behavior handle plain text
  };

  // Clean and convert pasted HTML to supported tags
  const cleanPastedHtml = (html) => {
    // Create a temporary element to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove Word/Office specific elements and styles
    const removeElements = temp.querySelectorAll('meta, link, style, xml, o\\:p, w\\:sdt, w\\:sdtpr, w\\:sdtcontent');
    removeElements.forEach(el => el.remove());
    
    // Process content recursively
    const processNode = (node, listContext = null) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        // Skip if just whitespace between elements
        if (!text.trim()) return '';
        return text;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) return '';
      
      const tag = node.tagName.toLowerCase();
      const style = node.getAttribute('style') || '';
      let content = Array.from(node.childNodes).map(n => processNode(n, listContext)).join('');
      
      // Skip empty content for block elements
      if (!content.trim() && ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tag)) {
        return '';
      }
      
      // Handle Word-specific list detection from styles
      const isList = style.includes('mso-list') || node.className?.includes('MsoList');
      
      switch (tag) {
        case 'h1':
          return `<h1>${content.trim()}</h1>\n\n`;
        case 'h2':
          return `<h2>${content.trim()}</h2>\n\n`;
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return `<h3>${content.trim()}</h3>\n\n`;
        case 'p':
          // Check if this is a Word list item
          if (isList) {
            return `<li>${content.trim()}</li>\n`;
          }
          // Check for numbered pattern at start (Word numbered lists)
          if (/^\s*\d+[\.\)]\s+/.test(content)) {
            const cleanContent = content.replace(/^\s*\d+[\.\)]\s+/, '');
            return `<li>${cleanContent.trim()}</li>\n`;
          }
          // Check for bullet pattern (Word bullet lists)
          if (/^\s*[•·-]\s+/.test(content) || /^\s*[o§]\s+/.test(content)) {
            const cleanContent = content.replace(/^\s*[•·\-o§]\s+/, '');
            return `<li>${cleanContent.trim()}</li>\n`;
          }
          return `<p>${content.trim()}</p>\n\n`;
        case 'strong':
        case 'b':
          return `<strong>${content}</strong>`;
        case 'em':
        case 'i':
          return `<em>${content}</em>`;
        case 'u':
          return `<u>${content}</u>`;
        case 'ul':
          return `<ul>\n${content}</ul>\n\n`;
        case 'ol':
          return `<ol>\n${content}</ol>\n\n`;
        case 'li':
          return `<li>${content.trim()}</li>\n`;
        case 'table':
          return `<table>\n${content}</table>\n\n`;
        case 'thead':
          return `<thead>\n${content}</thead>\n`;
        case 'tbody':
          return `<tbody>\n${content}</tbody>\n`;
        case 'tr':
          return `<tr>\n${content}</tr>\n`;
        case 'th':
          return `<th>${content.trim()}</th>\n`;
        case 'td':
          return `<td>${content.trim()}</td>\n`;
        case 'br':
          return '<br/>\n';
        case 'a':
          const href = node.getAttribute('href') || '#';
          if (href.startsWith('#') || href.includes('javascript:')) {
            return content; // Skip invalid links
          }
          return `<a href="${href}">${content}</a>`;
        case 'img':
          const src = node.getAttribute('src') || '';
          const alt = node.getAttribute('alt') || '';
          if (src && !src.startsWith('data:') && !src.startsWith('file:')) {
            return `<img src="${src}" alt="${alt}" />\n`;
          }
          return '';
        case 'blockquote':
          return `<blockquote>${content.trim()}</blockquote>\n\n`;
        case 'code':
        case 'pre':
          return `<code>${content}</code>`;
        case 'div':
        case 'span':
        case 'font':
        case 'section':
        case 'article':
          // Check for bold/italic in style
          let wrappedContent = content;
          if (style.includes('font-weight') && (style.includes('bold') || style.includes('700'))) {
            wrappedContent = `<strong>${wrappedContent}</strong>`;
          }
          if (style.includes('font-style') && style.includes('italic')) {
            wrappedContent = `<em>${wrappedContent}</em>`;
          }
          return wrappedContent;
        default:
          return content;
      }
    };
    
    let result = Array.from(temp.childNodes).map(n => processNode(n)).join('');
    
    // Post-processing: Group consecutive <li> elements into lists
    result = result.replace(/(<li>[\s\S]*?<\/li>\n)+/g, (match) => {
      // Check if it looks like numbered list
      if (match.includes('1.') || match.includes('1)')) {
        return `<ol>\n${match}</ol>\n\n`;
      }
      return `<ul>\n${match}</ul>\n\n`;
    });
    
    // Clean up excessive whitespace
    result = result
      .replace(/\n{3,}/g, '\n\n')
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<li>\s*<\/li>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    
    return result;
  };

  // Insert text formatting at cursor position
  const insertFormatting = (tag, style = '') => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    let formattedText;
    switch (tag) {
      case 'b':
        formattedText = `<strong>${selectedText || 'bold text'}</strong>`;
        break;
      case 'color':
        formattedText = `<span style="color: ${style}">${selectedText || 'colored text'}</span>`;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText || 'Heading'}</h2>\n`;
        break;
      case 'h3':
        formattedText = `<h3>${selectedText || 'Subheading'}</h3>\n`;
        break;
      case 'ul':
        formattedText = `<ul>\n  <li>${selectedText || 'List item'}</li>\n  <li>Another item</li>\n</ul>\n`;
        break;
      case 'ol':
        formattedText = `<ol>\n  <li>${selectedText || 'First item'}</li>\n  <li>Second item</li>\n</ol>\n`;
        break;
      case 'table':
        formattedText = `<table class="min-w-full border-collapse border border-gray-300">
<thead class="bg-gray-100">
<tr>
<th class="border border-gray-300 px-4 py-2">Header 1</th>
<th class="border border-gray-300 px-4 py-2">Header 2</th>
</tr>
</thead>
<tbody>
<tr>
<td class="border border-gray-300 px-4 py-2">Cell 1</td>
<td class="border border-gray-300 px-4 py-2">Cell 2</td>
</tr>
</tbody>
</table>\n`;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent = formData.content.substring(0, start) + formattedText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
    
    // Reset cursor position after state update
    setTimeout(() => {
      textarea.focus();
      const newPos = start + formattedText.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/blog/posts?page=${page}&limit=20`);
      setPosts(response.data.posts || response.data);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
      onStatsUpdate(response.data.pagination?.total || response.data.length);
    } catch (error) {
      toast.error("Failed to fetch blog posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Blog post updated successfully");
      } else {
        await axios.post(`${API}/admin/blog/posts`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Blog post created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please use JPG, PNG, or GIF");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/uploads/blog`, uploadFormData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store only the relative URL path (not full domain) so it works across deployments
      const imageUrl = response.data.file.url;
      setFormData({ ...formData, image: imageUrl });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Blog post deleted successfully");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      category: post.category,
      excerpt: post.excerpt,
      content: post.content,
      image: post.image,
      published: post.published
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      category: '',
      excerpt: '',
      content: '',
      image: '',
      published: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Blog Posts</h2>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            className="border-gray-700 text-gray-300 hover:bg-gray-800" 
            onClick={handleFixImageUrls}
            disabled={fixingIds}
          >
            <Image className="mr-2 h-4 w-4" />
            {fixingIds ? 'Fixing...' : 'Fix Images'}
          </Button>
          <Button 
            variant="outline" 
            className="border-gray-700 text-gray-300 hover:bg-gray-800" 
            onClick={handleFixPostIds}
            disabled={fixingIds}
          >
            <Wrench className="mr-2 h-4 w-4" />
            {fixingIds ? 'Fixing...' : 'Fix Post IDs'}
          </Button>
          <Button 
            variant="outline" 
            className="border-gray-700 text-gray-300 hover:bg-gray-800" 
            onClick={handleSeedPosts}
            disabled={seeding}
          >
            <Download className="mr-2 h-4 w-4" />
            {seeding ? 'Adding...' : 'Add Sample Posts'}
          </Button>
          <Button className="btn-primary text-white" onClick={() => { resetForm(); setDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add New Post
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <Card key={post.id} className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-white">{post.title}</CardTitle>
                  <CardDescription className="text-gray-400">{post.category}</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" className="border-gray-700" onClick={() => handleEdit(post)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-gray-700 text-red-400" onClick={() => handleDelete(post.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm">{post.excerpt}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-200">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-200">Category</label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g., Accident Prevention"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-200">Excerpt</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white"
                rows={2}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-200">Content</label>
              {/* Formatting Toolbar */}
              <div className="flex items-center gap-1 mb-2 p-2 bg-gray-800 rounded-t-lg border border-gray-700 border-b-0 flex-wrap">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('b')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Bold"
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('h2')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Heading"
                >
                  <Heading1 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('h3')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Subheading"
                >
                  <Heading2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('ul')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Bullet List"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('ol')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Numbered List"
                >
                  <ListOrdered className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => insertFormatting('table')}
                  className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                  title="Insert Table"
                >
                  <Table className="h-4 w-4" />
                </Button>
                <div className="relative">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="h-8 px-2 text-gray-300 hover:text-white hover:bg-gray-700"
                    title="Text Color"
                  >
                    <Palette className="h-4 w-4" />
                  </Button>
                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 p-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
                      <div className="grid grid-cols-4 gap-1">
                        {TEXT_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => {
                              insertFormatting('color', color.value);
                              setShowColorPicker(false);
                            }}
                            className="w-6 h-6 rounded border border-gray-600 hover:scale-110 transition-transform"
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-500 ml-2">Paste from Word/Docs to auto-format</span>
              </div>
              <Textarea
                ref={contentRef}
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                onPaste={handlePaste}
                className="bg-gray-800 border-gray-700 text-white rounded-t-none"
                rows={10}
                required
                placeholder="Write your blog content here. You can paste formatted text from Word or Google Docs!"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports HTML, tables, lists. Use <code className="bg-gray-700 px-1 rounded">[youtube:VIDEO_ID]</code> to embed videos.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-200">Image</label>
              <div className="space-y-3">
                {/* Upload Button */}
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept=".jpg,.jpeg,.png,.gif"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-gray-700 text-gray-300"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <span className="text-xs text-gray-500">JPG, PNG, GIF (max 5MB) • Auto-optimized to 1200×800px</span>
                </div>
                
                {/* Or Enter URL */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">or enter URL:</span>
                </div>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="https://example.com/image.jpg"
                />
                
                {/* Image Preview */}
                {formData.image && (
                  <div className="relative inline-block">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="h-32 w-auto rounded-lg border border-gray-700 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, image: ''})}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="rounded"
              />
              <label className="text-sm text-gray-200">Published</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-700">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary text-white">
                {editingPost ? 'Update' : 'Create'} Post
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
